import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import Loader from '../../../common/components/Loader'
import { emitReportDataIssueOpen } from '../../../constants/events'
import { ToastService } from '../../../services/toast.service'
import AssetMap from '../../CopAssetDetails/asset-map/AssetMap'
import { useSectionPermissions } from '../../Shared/PermissionAccessGuard/hooks/useSectionPermissions'
import {
    COP_SITE_DETAILS_ACCESSIBILITY,
    COP_SITE_DETAILS_BUTTON_TYPE,
    COP_SITE_DETAILS_ERROR,
    COP_SITE_DETAILS_LABELS,
} from '../constants/copSiteDetails.constants'
import { ButtonConfig, DetailFieldConfig } from '../copSiteDetails'
import {
    exportSiteDetails,
    getSiteIdFromUrl,
    SiteContactData,
    SiteDetailsResponse,
} from '../services/copSiteDetails.service'
import { ContactCard } from './ContactCard'
import { ContactsConfig,EditContactsModal } from './EditContactsModal'
import SiteDetailsActionBar from './SiteDetailsActionBar'
import { resolveFieldValue } from './siteDetailsHelpers'
import { buildActiveButtons, isPermittedButton, resolveSitePermissionFlags } from './siteDetailsPermissions'

type CopSiteDetailsSectionProps = {
    exportLabel: string
    enableTitle: boolean
    enableMap: boolean
    detailFields: DetailFieldConfig[]
    buttons: ButtonConfig[]
    emailLabel?: string
    siteData: SiteDetailsResponse | null
    hasApiError: boolean
    isLoading: boolean
    enableContacts?: boolean
    contactsConfig?: ContactsConfig | null
}

const CONTACT_KEYS = ['primaryContact', 'secondaryContact'] as const
const CONTACTS_SENTINEL = '__contacts__'
const EDIT_CONTACTS_ARIA_LABEL = 'Edit contacts'
const NO_VALUE_PLACEHOLDER = '-'
const OPEN_ASSETS_TREE_EVENT = 'cop:openAssetsTree'

const DEFAULT_FIELDS: DetailFieldConfig[] = [
    { displayName: COP_SITE_DETAILS_LABELS.CUSTOMER_SITE_NAME, dataKey: 'customerSiteName', displayUrl: '' },
    { displayName: COP_SITE_DETAILS_LABELS.INDUSTRY_USAGE, dataKey: 'industryUsage', displayUrl: '' },
    { displayName: COP_SITE_DETAILS_LABELS.OWNER, dataKey: 'owner', displayUrl: '' },
    { displayName: COP_SITE_DETAILS_LABELS.OPERATOR, dataKey: 'operator', displayUrl: '' },
    { displayName: COP_SITE_DETAILS_LABELS.CONTACT, dataKey: 'primaryContact', displayUrl: '' },
    { displayName: COP_SITE_DETAILS_LABELS.CONTACT, dataKey: 'secondaryContact', displayUrl: '' },
    { displayName: COP_SITE_DETAILS_LABELS.GEOLOCATION, dataKey: 'geolocation', displayUrl: '' },
    { displayName: COP_SITE_DETAILS_LABELS.SITE_STATUS, dataKey: 'operationalStatus', displayUrl: '' },
]

const isContactKey = (key: string): boolean =>
    (CONTACT_KEYS as readonly string[]).includes(key)

function computeRows(activeFields: DetailFieldConfig[]): DetailFieldConfig[] {
    let contactRowAdded = false
    return activeFields.reduce<DetailFieldConfig[]>((acc, field) => {
        if (isContactKey(field.dataKey)) {
            if (!contactRowAdded) {
                contactRowAdded = true
                acc.push({ displayName: field.displayName, dataKey: CONTACTS_SENTINEL, displayUrl: '' })
            }
        } else {
            acc.push(field)
        }
        return acc
    }, [])
}

export const CopSiteDetailsSection: React.FC<Readonly<CopSiteDetailsSectionProps>> = ({
    exportLabel,
    enableTitle,
    enableMap,
    detailFields,
    buttons,
    emailLabel,
    siteData,
    hasApiError,
    isLoading,
    enableContacts,
    contactsConfig,
}) => {

    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const [isContactsModalOpen, setIsContactsModalOpen] = useState(false)
    const [localSiteData, setLocalSiteData] = useState<SiteDetailsResponse | null>(siteData)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const sectionTitleId = useId()
    const { permissions, isLoading: permissionsLoading } = useSectionPermissions()

    useEffect(() => { setLocalSiteData(siteData) }, [siteData])

    useEffect(() => {
        if (!localSiteData?.name) return
        const breadcrumbItems = document.querySelectorAll('.cmp-breadcrumb__list .cmp-breadcrumb__item')
        if (breadcrumbItems.length > 0) {
            const lastItem = breadcrumbItems[breadcrumbItems.length - 1]
            const link = lastItem.querySelector<HTMLAnchorElement>('.cmp-breadcrumb__item-link')
            const target = link ?? lastItem
            target.textContent = localSiteData.name
        }
    }, [localSiteData?.name])

    useEffect(() => {
        if (!dropdownOpen) return
        const onDocumentClick = (e: MouseEvent): void => {
            if (!containerRef.current?.contains(e.target as Node)) {
                setDropdownOpen(false)
            }
        }
        document.addEventListener('click', onDocumentClick)
        return () => document.removeEventListener('click', onDocumentClick)
    }, [dropdownOpen])

    const handleExport = useCallback(async (exportType: 'Excel' | 'PDF'): Promise<void> => {
        const siteId = getSiteIdFromUrl()
        if (!siteId) return
        try {
            setIsExporting(true)
            await exportSiteDetails(siteId, exportType)
        } catch (error) {
            window.console.error('CopSiteDetails: Export failed', error)
            ToastService.error(COP_SITE_DETAILS_ERROR.EXPORT_FAILED)
        } finally {
            setIsExporting(false)
            setDropdownOpen(false)
        }
    }, [])

    const handleExportExcel = useCallback((): void => { void handleExport('Excel') }, [handleExport])
    const handleExportPDF = useCallback((): void => { void handleExport('PDF') }, [handleExport])
    const handleMobileExportExcel = useCallback((e: React.MouseEvent): void => {
        e.stopPropagation()
        void handleExport('Excel')
    }, [handleExport])
    const handleMobileExportPDF = useCallback((e: React.MouseEvent): void => {
        e.stopPropagation()
        void handleExport('PDF')
    }, [handleExport])
    const handleToggleDropdown = useCallback((): void => setDropdownOpen(open => !open), [])
    const handleOpenContactsModal = useCallback((): void => setIsContactsModalOpen(true), [])
    const handleCloseContactsModal = useCallback((): void => setIsContactsModalOpen(false), [])
    const handleContactsSaved = useCallback(
        (updated: Pick<SiteDetailsResponse, 'primaryContact' | 'secondaryContact'>): void => {
            setLocalSiteData(prev => prev ? { ...prev, ...updated } : prev)
        },
        [],
    )
    const activeFields = detailFields.length > 0 ? detailFields : DEFAULT_FIELDS

    const { canExportSiteDetails, canReportDataIssue, canViewAssetsTree, hasAssets, canEditContact } =
        resolveSitePermissionFlags(permissions, permissionsLoading, localSiteData?.assetsCount)

    const handleViewAssetsTree = useCallback((): void => {
        if (!hasAssets) return
        document.dispatchEvent(new CustomEvent(OPEN_ASSETS_TREE_EVENT))
    }, [hasAssets])

    const permittedButtons = useMemo<ButtonConfig[]>(
        () => buttons.filter(b => isPermittedButton(b, canViewAssetsTree, canReportDataIssue)),
        [buttons, canViewAssetsTree, canReportDataIssue],
    )

    const activeButtons = useMemo<ButtonConfig[]>(
        () => buildActiveButtons(permittedButtons, exportLabel, canExportSiteDetails),
        [permittedButtons, exportLabel, canExportSiteDetails],
    )

    const ctaButtons = useMemo(
        () => permittedButtons.filter(b => b.buttonType === COP_SITE_DETAILS_BUTTON_TYPE.CTA),
        [permittedButtons],
    )

    const rows = useMemo(() => computeRows(activeFields), [activeFields])
    const contactFields = useMemo(
        () => activeFields.filter(f => isContactKey(f.dataKey)),
        [activeFields],
    )

    // replace non-breaking spaces that come from the API so the title can wrap normally
    const title = (localSiteData?.name ?? '').replace(/\u00A0/g, ' ')
    const location = [localSiteData?.address, localSiteData?.city, localSiteData?.country].filter(Boolean).join(', ')
    const latitude = localSiteData?.latitude
    const longitude = localSiteData?.longitude
    const hasCoords = latitude != null && longitude != null
    const isExportDisabled = isExporting || !localSiteData || hasApiError
    const canEditContacts = Boolean(enableContacts && contactsConfig) && canEditContact
    const shouldRenderMap = enableMap && hasCoords && latitude != null && longitude != null

    const openReportDataIssue = useCallback((): void => {
        emitReportDataIssueOpen({
            contextType: 'site',
            contextId: localSiteData?.id,
            contextName: title || localSiteData?.customerSiteName || undefined,
        })
    }, [localSiteData?.id, localSiteData?.customerSiteName, title])

    const renderContactsRow = (field: DetailFieldConfig): JSX.Element => (
        <div key={`crow-${field.dataKey}`} className="row">
            <div className="label">
                {field.displayName}
                {canEditContacts && (
                    <button
                        type="button"
                        className="contact-edit-btn"
                        aria-label={EDIT_CONTACTS_ARIA_LABEL}
                        onClick={handleOpenContactsModal}
                    >
                        <span className="icon-edit" aria-hidden="true" />
                    </button>
                )}
            </div>
            <div className="contact-wrapper">
                {hasApiError ? <span>{NO_VALUE_PLACEHOLDER}</span> : renderContactCards()}
            </div>
        </div>
    )

    const renderContactCards = (): React.ReactNode => contactFields.map((cf) => {
        const contactData = localSiteData?.[cf.dataKey] as SiteContactData | null | undefined
        if (!contactData) return null
        return (
            <ContactCard
                key={cf.dataKey}
                contact={contactData}
                emailLabel={emailLabel}
            />
        )
    })

    const renderValueRow = (field: DetailFieldConfig): JSX.Element => {
        const resolvedValue = resolveFieldValue(field.dataKey, localSiteData, hasApiError)
        const content = field.displayUrl
            ? <a href={field.displayUrl}>{resolvedValue}</a>
            : resolvedValue
        return (
            <div key={`row-${field.dataKey}`} className="row">
                <div className="label">{field.displayName}</div>
                <div className="value">{content}</div>
            </div>
        )
    }

    const renderRow = (field: DetailFieldConfig): JSX.Element =>
        field.dataKey === CONTACTS_SENTINEL ? renderContactsRow(field) : renderValueRow(field)

    if (!permissionsLoading && !permissions.canViewSitesView) return null

    return (
        <section
            className="cop-site-details"
            ref={containerRef}
            aria-busy={isLoading}
            aria-label={COP_SITE_DETAILS_ACCESSIBILITY.SECTION_ARIA_LABEL}
            aria-labelledby={enableTitle && title ? sectionTitleId : undefined}
        >
            {isLoading ? (
                <div className="loader-wrapper">
                    <Loader />
                </div>
            ) : (
                <div className="card">
                    <div className="details">
                        <div className="top-section">
                            {enableTitle && (
                                <div>
                                    <div id={sectionTitleId} className="title">{title}</div>
                                    {location && <div className="subtitle">{location}</div>}
                                </div>
                            )}
                            <SiteDetailsActionBar
                                activeButtons={activeButtons}
                                ctaButtons={ctaButtons}
                                exportLabel={exportLabel}
                                dropdownOpen={dropdownOpen}
                                isExporting={isExporting}
                                isExportDisabled={isExportDisabled}
                                onToggleDropdown={handleToggleDropdown}
                                onExportExcel={handleExportExcel}
                                onExportPDF={handleExportPDF}
                                onMobileExportExcel={handleMobileExportExcel}
                                onMobileExportPDF={handleMobileExportPDF}
                                onReportIssueClick={openReportDataIssue}
                                onViewAssetsTree={handleViewAssetsTree}
                                assetsTreeDisabled={!hasAssets}
                            />
                        </div>

                        {shouldRenderMap && (
                            <div className="mobile-map">
                                <AssetMap latitude={latitude} longitude={longitude} />
                            </div>
                        )}

                        <div className="content">
                            {rows.map(renderRow)}
                        </div>
                    </div>

                    {shouldRenderMap && (
                        <div className="map">
                            <AssetMap latitude={latitude} longitude={longitude} />
                        </div>
                    )}
                </div>
            )}

            {isContactsModalOpen && canEditContacts && contactsConfig && (
                <EditContactsModal
                    config={contactsConfig}
                    siteData={localSiteData}
                    onClose={handleCloseContactsModal}
                    onSaved={handleContactsSaved}
                />
            )}
        </section>
    )
}
